/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Upload, FileText, Trash2, Search, Filter,
  CheckCircle, Clock, BookOpen, Archive,
  Download, Eye, RefreshCw, BarChart3, X, Plus,
  Monitor, AlertCircle, Loader, FolderUp
} from 'lucide-react';
import api from '../services/api';
import ViolationModal from '../components/ViolationModal';

const KnowledgeBase = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filters, setFilters] = useState({
    vertical: 'all',
    category: 'all',
    isProcessed: 'all',
    accessLevel: 'private',
    search: ''
  });
  const [violationData, setViolationData] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('documents');
  const [wikiEntries, setWikiEntries] = useState([]);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [wikiTab, setWikiTab] = useState('domain');
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ topic: '', content: '', tags: '', scope: 'private' });
  const [uploadForm, setUploadForm] = useState({
    vertical: user?.vertical || 'WMS',
    category: 'manual',
    title: '',
    description: '',
    tags: '',
    accessLevel: 'private'
  });

  const isAdmin = user?.userType === 'admin';

  // Batch Upload State
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchTab, setBatchTab] = useState('files'); // 'files' | 'folder'
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchCategory, setBatchCategory] = useState('');
  const [batchVertical, setBatchVertical] = useState(user?.vertical || 'WMS');
  const [batchAccessLevel, setBatchAccessLevel] = useState('vertical');
  const [folderGroups, setFolderGroups] = useState({}); // { folderName: { files: [], category: '', selected: true } }
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { total, completed, currentFolder, currentFolderTotal, currentFolderDone, log: [] }
  const [batchCancelled, setBatchCancelled] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null); // { folders: { name: { uploaded, failed, skipped } } }
  const batchFileInputRef = useRef(null);
  const batchFolderInputRef = useRef(null);
  const batchCancelRef = useRef(false);

  const FOLDER_CATEGORY_MAP = {
    'stock': 'stock',
    'buying': 'buying',
    'selling': 'selling',
    'accounts': 'accounts',
    'setting-up': 'setting-up',
    'human-resources': 'human-resources',
    'manufacturing': 'manufacturing',
    'crm': 'crm',
    'projects': 'projects',
    'regional': 'regional',
    'introduction': 'introduction',
    'using-erpnext': 'using-erpnext',
  };

  const toTitleCase = (str) => {
    return str.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  /**
   * Derive category from file's webkitRelativePath subfolder structure.
   * e.g. "docs/stock/article/file.md" → "stock-article"
   *      "docs/buying/file.md" → "buying"
   */
  const getCategory = (file) => {
    const relativePath = file.webkitRelativePath || '';
    if (!relativePath) return 'manual';

    const parts = relativePath.split('/');
    const baseFolder = parts[0];

    if (parts.length === 2) {
      // "stock/batch.md" → "stock"
      return FOLDER_CATEGORY_MAP[baseFolder] || baseFolder;
    }

    if (parts.length >= 3) {
      // "stock/articles/doc.md" → "stock-articles"
      const subFolder = parts[1];
      const baseCategory = FOLDER_CATEGORY_MAP[baseFolder] || baseFolder;
      return `${baseCategory}-${subFolder}`;
    }

    return 'manual';
  };

  const mapFolderToCategory = (folderName) => {
    const lower = folderName.toLowerCase();
    return FOLDER_CATEGORY_MAP[lower] || lower;
  };

  const ACCEPTED_EXTENSIONS = ['.md', '.txt', '.pdf', '.docx'];

  const filterAcceptedFiles = (files) => {
    return Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return ACCEPTED_EXTENSIONS.includes(ext);
    });
  };

  const handleBatchFileSelect = (e) => {
    const accepted = filterAcceptedFiles(e.target.files);
    setBatchFiles(accepted);
    setFolderGroups({});
  };

  const handleBatchFolderSelect = (e) => {
    const accepted = filterAcceptedFiles(e.target.files);
    const groups = {};
    accepted.forEach(file => {
      const category = getCategory(file);
      // Use the detected category as the group key for display
      const pathParts = file.webkitRelativePath.split('/');
      const baseFolder = pathParts[0];
      const subFolder = pathParts.length >= 3 ? pathParts[1] : null;
      const groupLabel = subFolder ? `${baseFolder}/${subFolder}` : baseFolder;

      if (!groups[groupLabel]) {
        groups[groupLabel] = {
          files: [],
          category: category,
          selected: true
        };
      }
      groups[groupLabel].files.push(file);
    });
    setFolderGroups(groups);
    setBatchFiles([]);
  };

  const toggleFolderSelection = (folderName) => {
    setFolderGroups(prev => ({
      ...prev,
      [folderName]: { ...prev[folderName], selected: !prev[folderName].selected }
    }));
  };

  const updateFolderCategory = (folderName, category) => {
    setFolderGroups(prev => ({
      ...prev,
      [folderName]: { ...prev[folderName], category }
    }));
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const uploadSingleFile = async (file, vertical, category, accessLevel, folderName) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('vertical', vertical);
    formData.append('category', category);
    const titleFromFile = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    formData.append('title', titleFromFile);
    formData.append('accessLevel', accessLevel);
    const tags = [vertical, folderName || category, 'documentation', 'v13'].filter(Boolean);
    formData.append('tags', JSON.stringify(tags));
    formData.append('userId', user.id);

    const response = await api.uploadDocument(formData);
    return response;
  };

  const startBatchUpload = async () => {
    setBatchUploading(true);
    setBatchCancelled(false);
    batchCancelRef.current = false;
    setBatchSummary(null);

    // Build file groups to upload
    let uploadGroups = [];
    if (batchTab === 'folder') {
      Object.entries(folderGroups).forEach(([folderName, group]) => {
        if (group.selected) {
          uploadGroups.push({
            folderName,
            category: group.category,
            files: group.files
          });
        }
      });
    } else {
      uploadGroups.push({
        folderName: batchCategory || 'manual',
        category: batchCategory || 'manual',
        files: batchFiles
      });
    }

    const totalFiles = uploadGroups.reduce((sum, g) => sum + g.files.length, 0);
    const progress = { total: totalFiles, completed: 0, currentFolder: '', currentFolderTotal: 0, currentFolderDone: 0, log: [] };
    setBatchProgress({ ...progress });

    const summary = {};

    for (const group of uploadGroups) {
      if (batchCancelRef.current) break;

      summary[group.folderName] = { uploaded: 0, failed: 0, skipped: 0 };
      progress.currentFolder = group.folderName;
      progress.currentFolderTotal = group.files.length;
      progress.currentFolderDone = 0;
      setBatchProgress({ ...progress });

      for (const file of group.files) {
        if (batchCancelRef.current) {
          summary[group.folderName].skipped += (group.files.length - progress.currentFolderDone);
          break;
        }

        try {
          const result = await uploadSingleFile(file, batchVertical, group.category, batchAccessLevel, group.folderName);
          if (result.success) {
            summary[group.folderName].uploaded++;
            progress.log.unshift({ name: file.name, status: 'success', folder: group.folderName });
          } else {
            summary[group.folderName].failed++;
            progress.log.unshift({ name: file.name, status: 'failed', folder: group.folderName, error: result.error });
          }
        } catch (err) {
          if (err?.response?.status === 429) {
            progress.log.unshift({ name: file.name, status: 'failed', folder: group.folderName, error: 'Rate limited - pausing 20s' });
            setBatchProgress({ ...progress });
            await delay(20000);
            // Retry once after rate limit pause
            try {
              const retry = await uploadSingleFile(file, batchVertical, group.category, batchAccessLevel, group.folderName);
              if (retry.success) {
                summary[group.folderName].uploaded++;
                progress.log.unshift({ name: file.name, status: 'success', folder: group.folderName });
              } else {
                summary[group.folderName].failed++;
                progress.log.unshift({ name: file.name, status: 'failed', folder: group.folderName });
              }
            } catch {
              summary[group.folderName].failed++;
              progress.log.unshift({ name: file.name, status: 'failed', folder: group.folderName, error: 'Retry failed' });
            }
          } else {
            summary[group.folderName].failed++;
            progress.log.unshift({ name: file.name, status: 'failed', folder: group.folderName, error: err.message });
          }
        }

        progress.completed++;
        progress.currentFolderDone++;
        setBatchProgress({ ...progress });
        await delay(1000);
      }
    }

    setBatchUploading(false);
    setBatchSummary({ folders: summary });
    loadData();
  };

  const cancelBatchUpload = () => {
    batchCancelRef.current = true;
    setBatchCancelled(true);
  };

  const resetBatchModal = () => {
    setShowBatchModal(false);
    setBatchTab('files');
    setBatchFiles([]);
    setBatchCategory('');
    setBatchVertical(user?.vertical || 'WMS');
    setBatchAccessLevel('vertical');
    setFolderGroups({});
    setBatchUploading(false);
    setBatchProgress(null);
    setBatchCancelled(false);
    setBatchSummary(null);
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const viewMap = {
    domain: 'domain_wiki',
    platform: 'scout_finds',
    user: 'my_notes'
  };

  const fetchWikiEntries = async (tab) => {
    setWikiLoading(true);
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const view = viewMap[tab] || 'domain_wiki';
      const res = await api.get(
        `/wiki/feed/knowledge_base?domain=${domain}&view=${view}`
      );
      setWikiEntries(res.data?.entries || res.entries || []);
    } catch (e) { setWikiEntries([]); }
    finally { setWikiLoading(false); }
  };

  useEffect(() => {
    if (activeMainTab === 'wiki') fetchWikiEntries(wikiTab);
  }, [activeMainTab, wikiTab]);

  const saveNote = async () => {
    try {
      await api.post('/wiki/entries', {
        topic: newNote.topic,
        content: newNote.content,
        tags: newNote.tags.split(',').map(t => t.trim()).filter(Boolean),
        scope: newNote.scope,
        domain: user?.vertical?.toLowerCase() || 'wms',
        entry_type: 'note',
        source_type: 'manual'
      });
      setShowAddNote(false);
      setNewNote({ topic: '', content: '', tags: '', scope: 'private' });
      fetchWikiEntries(wikiTab);
    } catch (e) { console.error('Save note failed:', e); }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Load documents
      const filterParams = {};
      if (filters.vertical !== 'all') filterParams.vertical = filters.vertical;
      if (filters.category !== 'all') filterParams.category = filters.category;
      if (filters.isProcessed !== 'all') filterParams.isProcessed = filters.isProcessed === 'true';
      if (filters.accessLevel !== 'all') filterParams.accessLevel = filters.accessLevel;

      const docsResponse = await api.getDocuments(filterParams);
      if (docsResponse.success) {
        setDocuments(docsResponse.documents);
      }

      // Load stats
      const statsResponse = await api.getDocumentStats(
        filters.vertical !== 'all' ? filters.vertical : null
      );
      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill title from filename if empty
      if (!uploadForm.title) {
        setUploadForm({
          ...uploadForm,
          title: file.name.replace(/\.[^/.]+$/, '') // Remove extension
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    if (!uploadForm.vertical) {
      alert('Please select a vertical');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('vertical', uploadForm.vertical);
      formData.append('category', uploadForm.category);
      formData.append('title', uploadForm.title || selectedFile.name);
      formData.append('description', uploadForm.description);
      formData.append('tags', JSON.stringify(uploadForm.tags.split(',').map(t => t.trim()).filter(t => t)));
      formData.append('userId', user.id);
      formData.append('accessLevel', uploadForm.accessLevel);

      const response = await api.uploadDocument(formData);

      if (response.success) {
        alert('Document uploaded successfully! Processing in background...');
        setShowUploadModal(false);
        setSelectedFile(null);
        setUploadForm({
          vertical: user?.vertical || 'WMS',
          category: 'manual',
          title: '',
          description: '',
          tags: '',
          accessLevel: 'private'
        });
        loadData();
      } else if (response.blocked || response.error === 'CONTENT_POLICY_VIOLATION' || response.error === 'ACCOUNT_SUSPENDED') {
        // Guard Rails violation detected — close upload modal and show violation modal
        setShowUploadModal(false);
        setViolationData(response);
      } else {
        alert('Upload failed: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await api.deleteDocument(documentId);
      if (response.success) {
        alert('Document deleted successfully');
        loadData();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed: ' + error.message);
    }
  };

  const handleReprocess = async (documentId) => {
    try {
      const response = await api.reprocessDocument(documentId);
      if (response.success) {
        alert('Document reprocessing started');
        loadData();
      }
    } catch (error) {
      console.error('Reprocess error:', error);
      alert('Reprocess failed: ' + error.message);
    }
  };

  const handleSearch = async () => {
    if (!filters.search.trim()) {
      loadData();
      return;
    }

    try {
      const response = await api.searchDocuments(
        filters.search,
        filters.vertical !== 'all' ? filters.vertical : null
      );
      if (response.success) {
        setDocuments(response.documents);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      pdf: '📄',
      docx: '📝',
      txt: '📃',
      md: '📋'
    };
    return icons[fileType] || '📄';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Main Tab Switcher */}
        <div style={{display:'flex', gap:'8px', marginBottom:'24px', borderBottom:'2px solid #e2e8f0', paddingBottom:'0'}}>
          {[{key:'documents',label:'📄 Documents'},{key:'wiki',label:'📚 Wiki'}].map(tab => (
            <button key={tab.key} onClick={()=>setActiveMainTab(tab.key)}
              style={{padding:'8px 20px', border:'none', background:'none', cursor:'pointer',
                fontWeight: activeMainTab===tab.key ? '600' : '400',
                color: activeMainTab===tab.key ? '#7c3aed' : '#64748b',
                borderBottom: activeMainTab===tab.key ? '2px solid #7c3aed' : '2px solid transparent',
                marginBottom:'-2px'}}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeMainTab === 'documents' && (<>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                <Archive className="w-10 h-10 text-cyan-600" />
                Knowledge Base
              </h1>
              <p className="text-gray-600">
                Manage domain-specific documents for intelligent AI recommendations
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowBatchModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center gap-2 font-medium shadow-lg"
              >
                <FolderUp className="w-5 h-5" />
                Batch Upload
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all flex items-center gap-2 font-medium shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Upload Document
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Documents</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Processed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.processed}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Embedded</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.embedded}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Processing</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats.total - stats.processed}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={filters.vertical}
              onChange={(e) => setFilters({ ...filters, vertical: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Verticals</option>
              <option value="WMS">WMS</option>
              <option value="iSeries">iSeries</option>
              <option value="Civil">Civil</option>
              <option value="Stitching">Stitching</option>
              <option value="ERPNext">ERPNext</option>
              <option value="General">General</option>
            </select>

            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Categories</option>
              <option value="manual">Manual</option>
              <option value="course">Course</option>
              <option value="certification">Certification</option>
              <option value="guide">Guide</option>
              <option value="reference">Reference</option>
              <option value="other">Other</option>
            </select>

            <select
              value={filters.isProcessed}
              onChange={(e) => setFilters({ ...filters, isProcessed: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Status</option>
              <option value="true">Processed</option>
              <option value="false">Processing</option>
              <option value="pending_local">Queued for Local</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={filters.accessLevel}
              onChange={(e) => setFilters({ ...filters, accessLevel: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Access Levels</option>
              <option value="private">Private (My Docs)</option>
              <option value="organization">Organization</option>
              <option value="global">Global (Platform)</option>
            </select>
          </div>

          <div className="flex gap-4 mt-4">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
            <button
              onClick={() => {
                setFilters({
                  vertical: 'all',
                  category: 'all',
                  isProcessed: 'all',
                  accessLevel: 'private',
                  search: ''
                });
                loadData();
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-cyan-600" />
            Documents ({documents.length})
          </h2>

          {documents.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Documents Found</h3>
              <p className="text-gray-600 mb-6">
                Upload your first document to build the knowledge base
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all"
              >
                Upload Document
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => (
                <div
                  key={doc._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="text-4xl">{getFileIcon(doc.fileType)}</div>
                      
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-800 mb-1">
                          {doc.title}
                        </h3>
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium">
                            {doc.vertical}
                          </span>
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {doc.category}
                          </span>
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                            {doc.fileType.toUpperCase()}
                          </span>
                          {/* Status Badge - supports hybrid mode statuses */}
                          {doc.status === 'processed' || doc.isProcessed ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Processed
                            </span>
                          ) : doc.status === 'pending_local' ? (
                            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Monitor className="w-3 h-3" />
                              Queued for Local
                            </span>
                          ) : doc.status === 'processing_local' ? (
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Loader className="w-3 h-3 animate-spin" />
                              Processing Locally
                            </span>
                          ) : doc.status === 'failed' ? (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Failed
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Processing...
                            </span>
                          )}
                        </div>

                        {doc.description && (
                          <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                        )}

                        {doc.tags && doc.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {doc.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{formatFileSize(doc.fileSize)}</span>
                          {doc.pageCount && <span>• {doc.pageCount} pages</span>}
                          <span>• Uploaded {new Date(doc.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!doc.isProcessed && (
                        <button
                          onClick={() => handleReprocess(doc._id)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Reprocess"
                        >
                          <RefreshCw className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Violation Modal */}
      <ViolationModal
        show={!!violationData}
        onClose={() => setViolationData(null)}
        violationData={violationData}
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Upload className="w-6 h-6 text-cyan-600" />
                  Upload Document
                </h2>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* File Upload - FIXED */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File *
                  </label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-cyan-500 transition-colors cursor-pointer"
                  >
                    <input
                      name="file"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.txt,.docx,.xlsx,.md,.markdown"
                      className="hidden"
                    />
                    <Upload className="w-12 h-12 text-gray-400 mb-2 mx-auto" />
                    <p className="text-sm text-gray-600 mb-1">
                      {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-500">
                      📄 Supported: PDF, TXT, DOCX, XLSX, Markdown (Max 10MB)
                    </p>
                  </div>
                </div>

                {/* Vertical */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vertical *
                  </label>
                  <select
                    value={uploadForm.vertical}
                    onChange={(e) => setUploadForm({ ...uploadForm, vertical: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="WMS">WMS</option>
                    <option value="iSeries">iSeries</option>
                    <option value="Civil">Civil</option>
                    <option value="Stitching">Stitching</option>
                    <option value="ERPNext">ERPNext</option>
                    <option value="General">General</option>
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="manual">Manual</option>
                    <option value="course">Course</option>
                    <option value="certification">Certification</option>
                    <option value="guide">Guide</option>
                    <option value="reference">Reference</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Scope (Access Level) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope *
                  </label>
                  <select
                    value={uploadForm.accessLevel}
                    onChange={(e) => setUploadForm({ ...uploadForm, accessLevel: e.target.value })}
                    disabled={!isAdmin}
                    title={isAdmin ? '' : 'Only admins can publish at Domain or Platform scope'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="private">🔒 Private (only me)</option>
                    <option value="vertical">🌐 Domain (everyone in {uploadForm.vertical})</option>
                    <option value="global">🌍 Platform (everyone)</option>
                  </select>
                  {!isAdmin && (
                    <p className="text-xs text-gray-500 mt-1">
                      Only admins can publish at Domain or Platform scope.
                    </p>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    placeholder="Document title (auto-filled from filename)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    placeholder="Brief description of the document..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                    placeholder="e.g., beginner, warehouse, inventory"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload Document
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Batch Upload Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FolderUp className="w-6 h-6 text-purple-600" />
                  Batch Upload
                </h2>
                <button
                  onClick={resetBatchModal}
                  disabled={batchUploading}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Show summary when complete */}
              {batchSummary ? (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Upload Complete
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium text-gray-700">Folder</th>
                          <th className="text-center px-4 py-2 font-medium text-green-700">Uploaded</th>
                          <th className="text-center px-4 py-2 font-medium text-red-700">Failed</th>
                          <th className="text-center px-4 py-2 font-medium text-gray-500">Skipped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(batchSummary.folders).map(([folder, counts]) => (
                          <tr key={folder} className="border-t border-gray-100">
                            <td className="px-4 py-2 font-medium text-gray-800">📁 {folder}</td>
                            <td className="text-center px-4 py-2 text-green-600">{counts.uploaded}</td>
                            <td className="text-center px-4 py-2 text-red-600">{counts.failed}</td>
                            <td className="text-center px-4 py-2 text-gray-500">{counts.skipped}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={resetBatchModal}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all font-medium"
                  >
                    Done
                  </button>
                </div>
              ) : batchUploading && batchProgress ? (
                /* Progress UI */
                <div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                      <span className="text-sm font-medium text-gray-700">{batchProgress.completed} / {batchProgress.total} files</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${batchProgress.total > 0 ? (batchProgress.completed / batchProgress.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Per-folder grouped progress */}
                  {batchTab === 'folder' && Object.keys(folderGroups).length > 0 && (
                    <div className="mb-4 space-y-2">
                      {Object.entries(folderGroups).filter(([, g]) => g.selected).map(([folderName, group]) => {
                        const isCurrent = batchProgress.currentFolder === folderName;
                        const isDone = batchProgress.log.filter(e => e.folder === folderName && e.status === 'success').length;
                        const total = group.files.length;
                        const pct = total > 0 ? Math.round((isDone / total) * 100) : 0;
                        const isPending = !isCurrent && isDone === 0;
                        return (
                          <div key={folderName} className={`p-2 rounded-lg text-sm ${isCurrent ? 'bg-purple-50 ring-1 ring-purple-200' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-gray-800">📁 {folderName} <span className="text-gray-500 font-normal text-xs">→ {group.category}</span></span>
                              <span className="text-xs text-gray-600">
                                {isPending ? 'pending' : `${isDone}/${total} files`}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${isCurrent ? 'bg-purple-500' : isDone === total ? 'bg-green-500' : 'bg-gray-300'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Current folder indicator for flat file mode */}
                  {batchTab === 'files' && (
                    <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm font-medium text-purple-800">
                        📁 {batchProgress.currentFolder} ({batchProgress.currentFolderDone}/{batchProgress.currentFolderTotal})
                      </p>
                    </div>
                  )}

                  <div className="border border-gray-200 rounded-lg mb-4 max-h-48 overflow-y-auto">
                    {batchProgress.log.length === 0 ? (
                      <p className="text-sm text-gray-500 p-4 text-center">Starting uploads...</p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {batchProgress.log.slice(0, 50).map((entry, idx) => (
                          <div key={idx} className="px-4 py-2 flex items-center gap-2 text-sm">
                            <span>
                              {entry.status === 'success' ? '✅' : entry.status === 'failed' ? '❌' : '⏭️'}
                            </span>
                            <span className="text-gray-500 text-xs">{entry.folder}/</span>
                            <span className="text-gray-800 truncate">{entry.name}</span>
                            {entry.error && <span className="text-red-500 text-xs ml-auto">{entry.error}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={cancelBatchUpload}
                    disabled={batchCancelled}
                    className="w-full px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium disabled:opacity-50"
                  >
                    {batchCancelled ? 'Cancelling after current file...' : 'Cancel Upload'}
                  </button>
                </div>
              ) : (
                /* Configuration UI */
                <div className="space-y-4">
                  {/* Tab Selector */}
                  <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => { setBatchTab('files'); setFolderGroups({}); }}
                      className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                        batchTab === 'files'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Select Files
                    </button>
                    <button
                      onClick={() => { setBatchTab('folder'); setBatchFiles([]); }}
                      className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                        batchTab === 'folder'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Select Folder
                    </button>
                  </div>

                  {/* Tab Content */}
                  {batchTab === 'files' ? (
                    <div>
                      <div
                        onClick={() => batchFileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors cursor-pointer"
                      >
                        <input
                          type="file"
                          ref={batchFileInputRef}
                          onChange={handleBatchFileSelect}
                          accept=".md,.txt,.pdf,.docx"
                          multiple
                          className="hidden"
                        />
                        <FolderUp className="w-12 h-12 text-gray-400 mb-2 mx-auto" />
                        <p className="text-sm text-gray-600 mb-1">
                          {batchFiles.length > 0 ? `${batchFiles.length} files selected` : 'Click to select multiple files'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Supported: .md, .txt, .pdf, .docx
                        </p>
                      </div>
                      {batchFiles.length > 0 && (
                        <div className="mt-3 max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                          {batchFiles.map((f, i) => (
                            <div key={i} className="px-3 py-1.5 text-sm text-gray-700 flex items-center gap-2">
                              <FileText className="w-3 h-3 text-gray-400" />
                              {f.name}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <input
                          type="text"
                          value={batchCategory}
                          onChange={(e) => setBatchCategory(e.target.value)}
                          placeholder="e.g. Manual, Guide, Reference..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div
                        onClick={() => batchFolderInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors cursor-pointer"
                      >
                        <input
                          type="file"
                          ref={batchFolderInputRef}
                          onChange={handleBatchFolderSelect}
                          accept=".md,.txt,.pdf,.docx"
                          multiple
                          webkitdirectory=""
                          className="hidden"
                        />
                        <FolderUp className="w-12 h-12 text-gray-400 mb-2 mx-auto" />
                        <p className="text-sm text-gray-600 mb-1">
                          {Object.keys(folderGroups).length > 0
                            ? `${Object.keys(folderGroups).length} subfolders detected`
                            : 'Click to select a folder'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Subfolders auto-detected as category groups
                        </p>
                      </div>

                      {Object.keys(folderGroups).length > 0 && (
                        <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                          {Object.entries(folderGroups).map(([folderName, group]) => (
                            <div key={folderName} className="p-3">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={group.selected}
                                  onChange={() => toggleFolderSelection(folderName)}
                                  className="w-4 h-4 text-purple-600 rounded"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-800">
                                      📁 {folderName}/ <span className="text-gray-500 font-normal">({group.files.length} files)</span>
                                    </span>
                                  </div>
                                  {group.selected && (
                                    <input
                                      type="text"
                                      value={group.category}
                                      onChange={(e) => updateFolderCategory(folderName, e.target.value)}
                                      className="mt-1 w-full px-3 py-1 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                                      placeholder="Category name"
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vertical */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vertical</label>
                    <select
                      value={batchVertical}
                      onChange={(e) => setBatchVertical(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="WMS">WMS</option>
                      <option value="iSeries">iSeries</option>
                      <option value="Civil">Civil</option>
                      <option value="Stitching">Stitching</option>
                      <option value="ERPNext">ERPNext</option>
                      <option value="General">General</option>
                    </select>
                  </div>

                  {/* Access Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Level</label>
                    <select
                      value={batchAccessLevel}
                      onChange={(e) => setBatchAccessLevel(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="global">Global</option>
                      <option value="vertical">Vertical</option>
                      <option value="workspace">Workspace</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  {/* KG Note */}
                  <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                    <strong>Knowledge Graph:</strong> Category names feed KG node generation. Consistent names across a batch ensure proper clustering. Tags auto-include vertical, folder name, "documentation", and "v13".
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <button
                      onClick={startBatchUpload}
                      disabled={
                        (batchTab === 'files' && batchFiles.length === 0) ||
                        (batchTab === 'folder' && Object.values(folderGroups).filter(g => g.selected).length === 0)
                      }
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                    >
                      <Upload className="w-5 h-5" />
                      Start Batch Upload
                    </button>
                    <button
                      onClick={resetBatchModal}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </>)}

        {/* Wiki Panel */}
        {activeMainTab === 'wiki' && (
          <div>
            <div style={{display:'flex',gap:'8px',marginBottom:'20px',flexWrap:'wrap',alignItems:'center'}}>
              {[{key:'domain',label:'🌐 Domain Wiki'},{key:'platform',label:'🔍 Scout Finds'},{key:'user',label:'✍️ My Notes'}].map(t=>(
                <button key={t.key} onClick={()=>setWikiTab(t.key)}
                  style={{padding:'6px 16px',borderRadius:'20px',
                    border: wikiTab===t.key ? 'none':'1px solid #e2e8f0',
                    background: wikiTab===t.key ? '#7c3aed':'white',
                    color: wikiTab===t.key ? 'white':'#64748b',
                    cursor:'pointer',fontSize:'14px'}}>
                  {t.label}
                </button>
              ))}
              <button onClick={()=>setShowAddNote(true)}
                style={{marginLeft:'auto',padding:'6px 16px',borderRadius:'20px',
                  border:'none',background:'#10b981',color:'white',cursor:'pointer'}}>
                + Add Note
              </button>
            </div>

            {showAddNote && (
              <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'12px',padding:'20px',marginBottom:'20px'}}>
                <h3 style={{margin:'0 0 16px'}}>Add Personal Note</h3>
                <input placeholder="Topic" value={newNote.topic}
                  onChange={e=>setNewNote({...newNote,topic:e.target.value})}
                  style={{width:'100%',padding:'8px 12px',borderRadius:'8px',border:'1px solid #e2e8f0',marginBottom:'8px',boxSizing:'border-box'}} />
                <textarea placeholder="Content..." value={newNote.content}
                  onChange={e=>setNewNote({...newNote,content:e.target.value})}
                  rows={4}
                  style={{width:'100%',padding:'8px 12px',borderRadius:'8px',border:'1px solid #e2e8f0',marginBottom:'8px',boxSizing:'border-box'}} />
                <input placeholder="Tags (comma separated)" value={newNote.tags}
                  onChange={e=>setNewNote({...newNote,tags:e.target.value})}
                  style={{width:'100%',padding:'8px 12px',borderRadius:'8px',border:'1px solid #e2e8f0',marginBottom:'8px',boxSizing:'border-box'}} />
                <select value={newNote.scope}
                  onChange={e=>setNewNote({...newNote,scope:e.target.value})}
                  style={{padding:'8px 12px',borderRadius:'8px',border:'1px solid #e2e8f0',marginBottom:'12px',display:'block'}}>
                  <option value="private">🔒 Private (only me)</option>
                  <option value="domain">🌐 Domain (all WMS users)</option>
                  <option value="platform">🌍 Platform (everyone)</option>
                </select>
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={saveNote}
                    style={{padding:'8px 20px',background:'#7c3aed',color:'white',border:'none',borderRadius:'8px',cursor:'pointer'}}>
                    Save Note
                  </button>
                  <button onClick={()=>setShowAddNote(false)}
                    style={{padding:'8px 20px',background:'#f1f5f9',border:'none',borderRadius:'8px',cursor:'pointer'}}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {wikiLoading ? (
              <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading wiki entries...</div>
            ) : wikiEntries.length === 0 ? (
              <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>
                {wikiTab==='user' && 'No personal notes yet. Click "+ Add Note" to start.'}
                {wikiTab==='domain' && 'No domain wiki entries yet. Scout Agent populates this daily.'}
                {wikiTab==='platform' && 'No platform entries yet. Check back after Scout Agent runs.'}
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                {wikiEntries.map(entry=>(
                  <div key={entry.id}
                    style={{background:'white',border:'1px solid #e2e8f0',borderRadius:'12px',padding:'16px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                      <h3 style={{margin:0,fontSize:'16px'}}>{entry.topic}</h3>
                      <span style={{fontSize:'12px',color:'#94a3b8'}}>
                        {new Date(entry.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {entry.summary && (
                      <p style={{margin:'0 0 8px',color:'#64748b',fontSize:'14px'}}>{entry.summary}</p>
                    )}
                    <div style={{display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center'}}>
                      {(entry.tags||[]).map((tag,i)=>(
                        <span key={i} style={{padding:'2px 8px',borderRadius:'12px',background:'#f1f5f9',color:'#64748b',fontSize:'12px'}}>{tag}</span>
                      ))}
                      <span style={{marginLeft:'auto',padding:'2px 8px',borderRadius:'12px',fontSize:'12px',
                        background: entry.source_type==='scout_agent'?'#dbeafe':entry.source_type==='seal_auto'?'#fef3c7':'#f0fdf4',
                        color: entry.source_type==='scout_agent'?'#1d4ed8':entry.source_type==='seal_auto'?'#92400e':'#166534'}}>
                        {entry.source_type==='scout_agent'?'🔍 Scout':entry.source_type==='seal_auto'?'🤖 SEAL':entry.source_type==='manual'?'✍️ You':'📋 Session'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;